import React, { useMemo, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setMainMode } from '../../slices/mainModeSlice'
import { selectMatch } from '../../slices/gameStatsSlice'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import PlayerStats from './PlayerStats'
import ThirdStats from './ThirdStats'
import HeatMap from './HeatMap'
import PlayerRatings from './PlayerRatings'
import PlayerDepthStats from './PlayerDepthStats'
import HaxGPTPanel from './HaxGPTPanel'
import { seekReplayEvidence, watchGoal } from '../../game2'
import { resolvePlayerName, toFiniteNumber, extractShotXg, estimateShotXg, isShotOnTarget } from '../../utils/gameStatsHelpers'
import MatchInsightPanels from './MatchInsightPanels'

const EMPTY_MATCH = {}
const EMPTY_GOALS = []

function MetricCard({ title, icon, left, right }) {
  const total = Math.max(1, Number(left || 0) + Number(right || 0))
  const pctLeft = Math.round((Number(left || 0) / total) * 100)
  const pctRight = 100 - pctLeft

  return (
    <Paper elevation={0} className="gs-metric-card gs-anim-pop">
      <div className="gs-metric-header">
        <span className="gs-metric-icon" aria-hidden="true">{icon}</span>
        <span className="gs-metric-title">{title}</span>
      </div>
      <div className="gs-metric-values">
        <div className="gs-metric-value red">{left}</div>
        <div className="gs-metric-bar">
          <div className="gs-metric-bar-fill red" style={{ width: `${pctLeft}%` }} />
          <div className="gs-metric-bar-fill blue" style={{ width: `${pctRight}%` }} />
        </div>
        <div className="gs-metric-value blue">{right}</div>
      </div>
      <div className="gs-metric-footer">
        <span className="red">{pctLeft}%</span>
        <span className="blue">{pctRight}%</span>
      </div>
    </Paper>
  )
}

function TeamBadge({ team = 'red' }) {
  const cls = team === 'blue' ? 'gs-badge gs-badge-blue gs-anim-pop' : 'gs-badge gs-badge-red gs-anim-pop'
  return <span className={cls} aria-hidden="true" />
}

const formatClock = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.max(0, seconds);
  const mins = Math.floor(total / 60);
  const secs = Math.round(total % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const normalizeTeam = (team) => {
  if (typeof team !== 'string') return null;
  const value = team.trim().toLowerCase();
  if (value === 'red') return 'Red';
  if (value === 'blue') return 'Blue';
  return null;
};

const buildTeamSets = (match) => {
  const makeSet = (list) => {
    const set = new Set();
    if (!Array.isArray(list)) return set;
    list.forEach((entry) => {
      const name = resolvePlayerName(entry);
      if (name) set.add(name.trim().toLowerCase());
    });
    return set;
  };
  return {
    red: makeSet(match?.redTeam),
    blue: makeSet(match?.blueTeam)
  };
};

const resolveShotTeam = (shot, teamSets) => {
  if (!shot || typeof shot !== 'object') return null;
  const explicitTeam = normalizeTeam(shot.team);
  if (explicitTeam) return explicitTeam;
  const shooter = resolvePlayerName(shot.player ?? shot.shooter ?? shot.author);
  if (shooter) {
    const key = shooter.trim().toLowerCase();
    if (teamSets.red.has(key)) return 'Red';
    if (teamSets.blue.has(key)) return 'Blue';
  }
  return null;
};

const resolveTeamFromShot = (save, match, teamSets) => {
  const shots = Array.isArray(match?.shots) ? match.shots : [];
  const idx = typeof save?.shotIndex === 'number' ? save.shotIndex : null;
  if (idx === null || idx < 0 || idx >= shots.length) return null;
  const shot = shots[idx];
  if (!shot) return null;
  const team = normalizeTeam(shot.savedByTeam);
  if (team) return team;
  const keeper = resolvePlayerName(shot.savedBy);
  if (!keeper) return null;
  const key = keeper.trim().toLowerCase();
  if (teamSets.red.has(key)) return 'Red';
  if (teamSets.blue.has(key)) return 'Blue';
  return null;
};

const computeTeamSaves = (match) => {
  if (!match) return { red: 0, blue: 0 };
  const teamSets = buildTeamSets(match);
  let red = 0;
  let blue = 0;

  const addSave = (team) => {
    const normalized = normalizeTeam(team);
    if (normalized === 'Red') {
      red += 1;
      return true;
    }
    if (normalized === 'Blue') {
      blue += 1;
      return true;
    }
    return false;
  };

  const saves = Array.isArray(match.saves) ? match.saves : [];
  saves.forEach((save) => {
    if (!save) return;
    if (addSave(save.team)) return;
    const keeper = resolvePlayerName(save.player ?? save.keeper ?? save.nick ?? save.by ?? save);
    if (keeper) {
      const key = keeper.trim().toLowerCase();
      if (teamSets.red.has(key)) {
        red += 1;
        return;
      }
      if (teamSets.blue.has(key)) {
        blue += 1;
        return;
      }
    }
    const fromShot = resolveTeamFromShot(save, match, teamSets);
    if (fromShot) addSave(fromShot);
  });

  if (red === 0 && blue === 0) {
    const shots = Array.isArray(match.shots) ? match.shots : [];
    shots.forEach((shot) => {
      if (!shot) return;
      if (addSave(shot.savedByTeam)) return;
      const outcome = typeof shot.outcome === 'string' ? shot.outcome.toLowerCase() : '';
      if (outcome !== 'saved' && !shot.savedBy) return;
      const keeper = resolvePlayerName(shot.savedBy);
      if (!keeper) return;
      const key = keeper.trim().toLowerCase();
      if (teamSets.red.has(key)) {
        red += 1;
      } else if (teamSets.blue.has(key)) {
        blue += 1;
      }
    });
  }

  return { red, blue };
};

const getTeamSaveTotals = (match) => {
  const computed = computeTeamSaves(match);
  const reportedRed = toFiniteNumber(match?.savesRed);
  const reportedBlue = toFiniteNumber(match?.savesBlue);
  return {
    red: Math.max(computed.red, reportedRed !== null ? reportedRed : 0),
    blue: Math.max(computed.blue, reportedBlue !== null ? reportedBlue : 0)
  };
};

const computeTeamXgTotals = (match) => {
  const totals = { red: 0, blue: 0 };
  if (!match) return totals;
  const teamSets = buildTeamSets(match);
  const shots = Array.isArray(match.shots) ? match.shots : [];
  shots.forEach((shot) => {
    if (!shot) return;
    const team = resolveShotTeam(shot, teamSets);
    if (!team) return;
    let xg = extractShotXg(shot);
    if (!Number.isFinite(xg) || xg <= 0) {
      xg = estimateShotXg(shot, {
        stadium: match.stadium,
        redTeam: match.redTeam,
        blueTeam: match.blueTeam
      });
    }
    if (!Number.isFinite(xg) || xg < 0) return;
    if (team === 'Red') {
      totals.red += xg;
    } else if (team === 'Blue') {
      totals.blue += xg;
    }
  });
  return totals;
};

const resolveFallbackOffTarget = (match, team) => {
  const keys = team === 'Red'
    ? ['shotsOffTargetRed', 'shotsWideRed', 'missesRed', 'shotsMissedRed']
    : ['shotsOffTargetBlue', 'shotsWideBlue', 'missesBlue', 'shotsMissedBlue'];
  for (const key of keys) {
    const value = toFiniteNumber(match?.[key]);
    if (value !== null) return value;
  }
  return 0;
};

const computeTeamShotBreakdown = (match) => {
  const breakdown = {
    red: { on: 0, off: 0 },
    blue: { on: 0, off: 0 }
  };
  if (!match) return breakdown;
  const teamSets = buildTeamSets(match);
  const shots = Array.isArray(match.shots) ? match.shots : [];
  shots.forEach((shot) => {
    if (!shot) return;
    const team = resolveShotTeam(shot, teamSets);
    if (!team) return;
    const onTarget = isShotOnTarget(shot);
    if (team === 'Red') {
      if (onTarget) breakdown.red.on += 1;
      else breakdown.red.off += 1;
    } else if (team === 'Blue') {
      if (onTarget) breakdown.blue.on += 1;
      else breakdown.blue.off += 1;
    }
  });

  const fallbackRedOn = toFiniteNumber(match?.shotsRed);
  const fallbackBlueOn = toFiniteNumber(match?.shotsBlue);
  if (fallbackRedOn !== null) breakdown.red.on = Math.max(breakdown.red.on, fallbackRedOn);
  if (fallbackBlueOn !== null) breakdown.blue.on = Math.max(breakdown.blue.on, fallbackBlueOn);
  const fallbackRedOff = resolveFallbackOffTarget(match, 'Red');
  const fallbackBlueOff = resolveFallbackOffTarget(match, 'Blue');
  breakdown.red.off = Math.max(breakdown.red.off, fallbackRedOff || 0);
  breakdown.blue.off = Math.max(breakdown.blue.off, fallbackBlueOff || 0);

  return breakdown;
};

const passEventTick = (event) => {
  if (!event || typeof event !== 'object') return null;
  const candidates = [event.replayTick, event.matchTick, event.tick, event.gameTicks];
  for (const candidate of candidates) {
    const tick = toFiniteNumber(candidate);
    if (tick !== null) return tick;
  }
  return null;
};

const samePlayerName = (left, right) => {
  const a = resolvePlayerName(left);
  const b = resolvePlayerName(right);
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
};

const resolvePassTeam = (pass, teamSets) => {
  const explicit = normalizeTeam(pass?.team ?? pass?.lastShot?.team);
  if (explicit) return explicit;
  const passer = resolvePlayerName(pass?.player ?? pass?.passer ?? pass?.author ?? pass?.from ?? pass?.source);
  if (!passer) return null;
  const key = passer.trim().toLowerCase();
  if (teamSets.red.has(key)) return 'Red';
  if (teamSets.blue.has(key)) return 'Blue';
  return null;
};

const computeLinkedKeyPasses = (match, teamSets) => {
  const totals = { red: 0, blue: 0 };
  const passes = Array.isArray(match?.passes) ? match.passes : [];
  const shots = Array.isArray(match?.shots) ? match.shots : [];
  if (!passes.length || !shots.length) return totals;
  const usedShots = new Set();

  passes.forEach((pass) => {
    if (!pass || typeof pass !== 'object') return;
    const passer = resolvePlayerName(pass.player ?? pass.passer ?? pass.author ?? pass.from ?? pass.source ?? pass.lastShot?.player);
    const passTeam = resolvePassTeam(pass, teamSets);
    if (!passer || !passTeam) return;
    const passTick = passEventTick(pass.lastShot) ?? passEventTick(pass);
    let linkedIndex = -1;

    // Some exports keep a direct reference to the shot created by a pass.
    if (pass.shot && typeof pass.shot === 'object') {
      linkedIndex = shots.findIndex((shot, index) => (
        !usedShots.has(index)
        && shot === pass.shot
        && !samePlayerName(shot?.player ?? shot?.shooter ?? shot?.author, passer)
      ));
    }

    if (linkedIndex < 0 && passTick !== null) {
      let bestDistance = Number.POSITIVE_INFINITY;
      shots.forEach((shot, index) => {
        if (!shot || usedShots.has(index)) return;
        const shooter = resolvePlayerName(shot.player ?? shot.shooter ?? shot.author ?? shot.scorer);
        if (!shooter || samePlayerName(shooter, passer)) return;
        const shotTeam = resolveShotTeam(shot, teamSets);
        if (shotTeam !== passTeam) return;
        const shotTick = passEventTick(shot);
        if (shotTick === null || shotTick < passTick) return;
        const distance = shotTick - passTick;
        if (distance > 240 || distance >= bestDistance) return;
        bestDistance = distance;
        linkedIndex = index;
      });
    }

    if (linkedIndex < 0) return;
    usedShots.add(linkedIndex);
    if (passTeam === 'Red') totals.red += 1;
    else totals.blue += 1;
  });

  return totals;
};

const computeReportedKeyPasses = (match, teamSets) => {
  const totals = { red: 0, blue: 0 };
  const players = [
    ...(Array.isArray(match?.player) ? match.player : []),
    ...(Array.isArray(match?.players) ? match.players : [])
  ];
  const byPlayer = new Map();
  players.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const name = resolvePlayerName(entry);
    if (!name) return;
    const metrics = entry.metrics || entry.stats || {};
    const value = toFiniteNumber(
      metrics.keyPasses ?? metrics.key_passes ?? entry.keyPasses ?? entry.key_passes
    );
    if (value === null) return;
    const key = name.trim().toLowerCase();
    const team = normalizeTeam(entry.team ?? metrics.teamKey ?? metrics.team) || (() => {
      if (teamSets.red.has(key)) return 'Red';
      if (teamSets.blue.has(key)) return 'Blue';
      return null;
    })();
    if (!team) return;
    const existing = byPlayer.get(key);
    if (!existing || value > existing.value) byPlayer.set(key, { team, value });
  });

  byPlayer.forEach(({ team, value }) => {
    if (team === 'Red') totals.red += Math.max(0, value);
    else if (team === 'Blue') totals.blue += Math.max(0, value);
  });
  return totals;
};

const computeTeamKeyPasses = (match) => {
  if (!match) return { red: 0, blue: 0 };
  const teamSets = buildTeamSets(match);
  const reported = computeReportedKeyPasses(match, teamSets);
  const linked = computeLinkedKeyPasses(match, teamSets);
  const directRed = toFiniteNumber(match.keyPassesRed ?? match.key_passes_red);
  const directBlue = toFiniteNumber(match.keyPassesBlue ?? match.key_passes_blue);
  return {
    red: Math.max(reported.red, linked.red, directRed !== null ? directRed : 0),
    blue: Math.max(reported.blue, linked.blue, directBlue !== null ? directBlue : 0)
  };
};

function GoalTimeline({ goals = [], totalSeconds = null, onNavigateGoal = null }) {
  if (!goals.length) {
    return <div className="gs-goal-empty gs-anim-fade">No goals yet — clean sheet vibes.</div>;
  }
  const maxClock = goals.reduce((acc, goal) => {
    const clock = Number(goal?.clockSeconds);
    return Number.isFinite(clock) && clock > acc ? clock : acc;
  }, 0);
  const duration = Number(totalSeconds);
  const total = Number.isFinite(duration) && duration > 0 ? duration : (maxClock || goals.length || 1);
  const segments = total >= 1800 ? 6 : total >= 900 ? 4 : 3;
  const axisStops = Array.from({ length: segments + 1 }, (_v, idx) => Math.min(total, (total / segments) * idx));

  return (
    <div className="gs-goal-timeline gs-anim-glide">
      <div className="gs-goal-track">
        {goals.map((goal, idx) => {
          const clock = Number(goal?.clockSeconds);
          const ratio = Number.isFinite(clock) && total > 0
            ? Math.min(1, Math.max(0, clock / total))
            : Math.min(1, Math.max(0, idx / Math.max(1, goals.length - 1)));
          const teamClass = goal?.for === 'Blue' ? 'blue' : 'red';
          const verticalShift = teamClass === 'blue' ? 'down' : 'up';
          const label = goal?.scorer ? `${goal.scorer}${goal?.assist ? ` (A: ${goal.assist.player || goal.assist})` : ''}` : 'Goal';
          const score = Array.isArray(goal?.currentScore) ? `${goal.currentScore[0]}-${goal.currentScore[1]}` : '';
          const clockLabel = Number.isFinite(clock) ? formatClock(clock) : null;
          const handleClick = (event) => {
            event?.stopPropagation?.();
            const goalIdx = Number.isFinite(goal?.goalIndex) ? goal.goalIndex : idx;
            if (typeof onNavigateGoal === 'function') {
              onNavigateGoal(goalIdx);
            }
          };
          return (
            <div
              tabIndex="0"
              key={`${idx}-${goal?.goalIndex ?? idx}`}
              className={`gs-goal-marker ${teamClass} ${verticalShift}`}
              style={{ left: `${ratio * 100}%` }}
              onClick={handleClick}
            >
              <div className="gs-goal-pulse" />
              <div className="gs-goal-label">
                <span className="gs-goal-score">{score}</span>
                <span className="gs-goal-player">{label}</span>
                {clockLabel && <span className="gs-goal-clock">{clockLabel}</span>}
              </div>
            </div>
          );
        })}
        <div className="gs-goal-axis">
          {axisStops.map((stop, idx) => (
            <div className="gs-goal-tick" style={{ left: `${(stop / total) * 100}%` }} key={`tick-${idx}`}>
              <span>{formatClock(stop)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function GameStatsPro() {
  const dispatch = useDispatch()
  const matches = useSelector((s) => s.gameStats.matches) || []
  const mtc = useSelector((s) => s.gameStats.selectedMatch) || 0
  const redTeamNames = useSelector((s) => s.gameStats.redTeamNames)
  const blueTeamNames = useSelector((s) => s.gameStats.blueTeamNames)
  const match = matches[mtc] || EMPTY_MATCH

  const [tab, setTab] = useState(0)
  const setTabIndex = (_e, v) => setTab(v)

  const close = useCallback(() => dispatch(setMainMode('home')), [dispatch])
  const nextMatch = useCallback(() => mtc < (matches.length - 1) && dispatch(selectMatch(mtc + 1)), [dispatch, mtc, matches.length])
  const prevMatch = useCallback(() => mtc > 0 && dispatch(selectMatch(mtc - 1)), [dispatch, mtc])

  const teamNames = useMemo(() => {
    const redList = Array.isArray(redTeamNames) ? redTeamNames : []
    const blueList = Array.isArray(blueTeamNames) ? blueTeamNames : []
    return {
      red: redList[mtc] || 'RED',
      blue: blueList[mtc] || 'BLUE'
    }
  }, [redTeamNames, blueTeamNames, mtc])

  const goals = Array.isArray(match?.goals) ? match.goals : EMPTY_GOALS

  const overview = useMemo(() => {
    const possRed = Number(match.possRed || 0)
    const possBlue = Number(match.possBlue || 0)
    const possTotal = Math.max(1, possRed + possBlue)
    const pRed = Math.round((possRed / possTotal) * 1000) / 10
    const pBlue = Math.round((possBlue / possTotal) * 1000) / 10
    const effRedTicks = Number(match.effectivePossRed || 0)
    const effBlueTicks = Number(match.effectivePossBlue || 0)
    const effTotal = Math.max(1, effRedTicks + effBlueTicks)
    const effRed = Math.round((effRedTicks / effTotal) * 1000) / 10
    const effBlue = Math.round((effBlueTicks / effTotal) * 1000) / 10
    const kicksR = Number(match.kicksRed || 0), kicksB = Number(match.kicksBlue || 0)
    const passesR = Number(match.passesRed || 0), passesB = Number(match.passesBlue || 0)
    const duelsWonR = Number(match.duelWinsRed || 0)
    const duelsWonB = Number(match.duelWinsBlue || 0)
    const shotTotals = computeTeamShotBreakdown(match)
    const saveTotals = getTeamSaveTotals(match)
    const keyPassTotals = computeTeamKeyPasses(match)
    const xgTotals = computeTeamXgTotals(match)
    return {
      pRed,
      pBlue,
      effRed,
      effBlue,
      kicksR,
      kicksB,
      passesR,
      passesB,
      duelsWonR,
      duelsWonB,
      shotsOnR: shotTotals.red.on,
      shotsOnB: shotTotals.blue.on,
      shotsOffR: shotTotals.red.off,
      shotsOffB: shotTotals.blue.off,
      savesR: saveTotals.red,
      savesB: saveTotals.blue,
      keyPassesR: keyPassTotals.red,
      keyPassesB: keyPassTotals.blue,
      xgR: xgTotals.red.toFixed(2),
      xgB: xgTotals.blue.toFixed(2)
    }
  }, [match])

  const metricCards = useMemo(() => ([
    { title: 'Kicks', icon: '⚡', left: overview.kicksR, right: overview.kicksB },
    { title: 'Passes', icon: '🔁', left: overview.passesR, right: overview.passesB },
    { title: 'Shots On Target', icon: '🎯', left: overview.shotsOnR, right: overview.shotsOnB },
    { title: 'Shots Off Target', icon: '💨', left: overview.shotsOffR, right: overview.shotsOffB },
    { title: 'Duels Won', icon: '🛡️', left: overview.duelsWonR, right: overview.duelsWonB },
    { title: 'Expected Goals', icon: '📈', left: overview.xgR, right: overview.xgB },
    { title: 'Saves', icon: '🧱', left: overview.savesR, right: overview.savesB },
    { title: 'Key Passes', icon: '↗️', left: overview.keyPassesR, right: overview.keyPassesB }
  ]), [overview])

  const goToGoal = useCallback((goalIndex) => {
    const goal = goals[goalIndex] || goals.find((entry) => Number(entry?.goalIndex) === Number(goalIndex));
    const goalTick = Number(goal?.replayTick ?? goal?.shot?.replayTick ?? goal?.gameTicks ?? goal?.shot?.tick);
    let jumped = false;
    if (Number.isFinite(goalTick)) {
      try { jumped = seekReplayEvidence(goalTick); } catch (_) {}
    }
    // Older sidecars may not contain replay ticks. Keep their timeline
    // fallback, but use the same lead-in and playback behavior when it does.
    if (!jumped) {
      try { watchGoal(goalIndex); } catch (_) {}
      try {
        const playIcon = document.querySelector('[data-hook="playicon"]');
        const playButton = document.querySelector('[data-hook="play"]');
        if (playIcon && playButton && playIcon.textContent && playIcon.textContent.trim() !== '⏸') playButton.click();
      } catch (_) {}
    }
    requestAnimationFrame(() => close());
  }, [close, goals]);

  const goToMoment = useCallback((tick) => {
    if (!Number.isFinite(Number(tick))) return;
    try { seekReplayEvidence(Number(tick)); } catch (_) {}
    requestAnimationFrame(() => close());
  }, [close]);

  return (
    <div id="greyer-pro" className="gs-pro-overlay" onClick={(e) => { if (e.target.id === 'greyer-pro') close() }}>
      <Paper elevation={0} id="game-stats-pro" className="gs-pro-panel gs-anim-rise">
        <div className="gs-pro-header">
          <div className="gs-pro-header-row">
            <div className="gs-pro-team gs-pro-team-red gs-anim-glow">
              <TeamBadge team="red" />
              <span className="gs-pro-team-name">{teamNames.red}</span>
            </div>
            <div className="gs-pro-score gs-anim-pulse">
              <span>{match.scoreRed || 0}</span>
              <span className="gs-pro-score-sep">:</span>
              <span>{match.scoreBlue || 0}</span>
            </div>
            <div className="gs-pro-team gs-pro-team-blue gs-anim-glow">
              <span className="gs-pro-team-name">{teamNames.blue}</span>
              <TeamBadge team="blue" />
            </div>
          </div>
          <GoalTimeline goals={goals} totalSeconds={(match.gameTicks || 0) / 60} onNavigateGoal={goToGoal} />
          <div className="gs-pro-sub">
            <div>Stadium: {match?.stadium?.name || 'Unknown'}</div>
            <div>Mode: {match.spaceMode ? 'Space' : (match.realSoccerMode ? 'Real Soccer' : 'Classic')}</div>
            <div className="gs-pro-spacer" />
            <div className="gs-pro-nav">
              <Button size="small" variant="outlined" disabled={mtc === 0} onClick={prevMatch}>Prev</Button>
              <Typography component="span" sx={{ mx: 1 }}>Match {mtc + 1}/{matches.length || 1}</Typography>
              <Button size="small" variant="outlined" disabled={mtc >= (matches.length - 1)} onClick={nextMatch}>Next</Button>
              <Button size="small" variant="contained" color="error" onClick={close} sx={{ ml: 1 }}>Close</Button>
            </div>
          </div>
        </div>

        <Tabs value={tab} onChange={setTabIndex} className="gs-pro-tabs">
          <Tab label="Overview" />
          <Tab label="Players" />
          <Tab label="Depth Stats" />
          <Tab label="Ratings" />
          <Tab label="Hax GPT" />
        </Tabs>

        {tab === 0 && (
          <Box className="gs-pro-tab gs-anim-fade">
            <Grid container spacing={2}>
              <Grid item xs={12} className="gs-pro-possession">
                <div className="gs-pro-pos-label">Possession</div>
                <div className="gs-pro-pos-bar">
                  <div className="gs-pro-pos-red" style={{ width: `${overview.pRed}%` }} />
                  <div className="gs-pro-pos-blue" style={{ width: `${overview.pBlue}%` }} />
                </div>
                <div className="gs-pro-pos-nums">
                  <span className="red">{overview.pRed}%</span>
                  <span className="blue">{overview.pBlue}%</span>
                </div>
              </Grid>
              <Grid item xs={12} className="gs-pro-possession">
                <div className="gs-pro-pos-label">Effective Possession</div>
                <div className="gs-pro-pos-bar">
                  <div className="gs-pro-pos-red" style={{ width: `${overview.effRed}%` }} />
                  <div className="gs-pro-pos-blue" style={{ width: `${overview.effBlue}%` }} />
                </div>
                <div className="gs-pro-pos-nums">
                  <span className="red">{overview.effRed}%</span>
                  <span className="blue">{overview.effBlue}%</span>
                </div>
              </Grid>
              {metricCards.map(({ title, icon, left, right }) => (
                <Grid item xs={12} md={6} key={title}>
                  <MetricCard title={title} icon={icon} left={left} right={right} />
                </Grid>
              ))}
              <Grid item xs={12}>
                <MatchInsightPanels match={match} onSeek={goToMoment} />
              </Grid>
              <Grid item xs={12} className="gs-thirds-overview">
                <div className="match-insight-grid match-insight-grid--shot-only">
                  <section className="insight-card territory-control-card">
                    <div className="insight-section-header">
                      <div className="insight-section-title">Territory Control Map</div>
                      <div className="insight-section-subtitle">Control by territory</div>
                    </div>
                    <ThirdStats embed />
                  </section>
                </div>
              </Grid>
            </Grid>
          </Box>
        )}

        {tab === 1 && (
          <Box className="gs-pro-tab gs-anim-fade">
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} lg={8}>
                <Paper elevation={0} className="gs-pro-table-wrap gs-anim-pop">
                  <div className="gs-pro-table-scroll gs-pro-table-scroll--compact">
                    <table className="gs-pro-table gs-pro-table--compact">
                      <PlayerStats />
                    </table>
                  </div>
                </Paper>
              </Grid>
              <Grid item xs={12} lg={4}>
                <div className="gs-heatmap-plain gs-anim-pop">
                  <div className="gs-pro-heatmap-title">Player Heatmap</div>
                  <div className="gs-pro-heatmap-body">
                    <HeatMap />
                  </div>
                </div>
              </Grid>
            </Grid>
          </Box>
        )}

        {tab === 2 && (
          <Box className="gs-pro-tab gs-anim-fade">
            <Paper elevation={0} className="gs-pro-table-wrap gs-anim-pop">
              <div className="gs-pro-table-scroll">
                <PlayerDepthStats />
              </div>
            </Paper>
          </Box>
        )}

        {tab === 3 && (
          <Box className="gs-pro-tab gs-anim-fade">
            <Paper elevation={0} className="gs-pro-card gs-pro-rating-wrapper gs-anim-pop">
              <PlayerRatings />
            </Paper>
          </Box>
        )}

        {tab === 4 && (
          <Box className="gs-pro-tab gs-pro-tab--haxgpt gs-anim-fade">
            <Paper elevation={0} className="gs-pro-card gs-pro-card--haxgpt gs-anim-pop">
              <HaxGPTPanel />
            </Paper>
          </Box>
        )}

      </Paper>
    </div>
  )
}
